import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import useAutoRefresh from '../../hooks/useAutoRefresh';
import AdminLayout from '../../components/layout/AdminLayout';
import PayrollLayout from '../../components/layout/PayrollLayout';
import ComplianceIndicator from '../../components/ComplianceIndicator';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Edit2,
  Save,
  X,
  Trash2,
  Printer,
  FileArchive,
  User,
  Eye,
  MapPin,
  Calendar,
  AlertTriangle,
  Search,
  ChevronDown,
  ChevronUp,
  Camera,
  CalendarRange,
  Users as UsersIcon,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatUK } from '../../lib/ukTime';
import { toast } from 'sonner';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import WorkHoursHistory from '../../components/WorkHoursHistory';
import StaffPayrollInfo from '../../components/StaffPayrollInfo';
import { useAuth } from '../../contexts/AuthContext';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { cn } from '../../lib/utils';
import ClockSiteTotals from '../../components/ClockSiteTotals';
import DayPdfButton from '../../components/DayPdfButton';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTHS = [
  { value: '01', label: 'January' },
  { value: '02', label: 'February' },
  { value: '03', label: 'March' },
  { value: '04', label: 'April' },
  { value: '05', label: 'May' },
  { value: '06', label: 'June' },
  { value: '07', label: 'July' },
  { value: '08', label: 'August' },
  { value: '09', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

// Entry-level filter helpers. Each predicate receives a "view entry"
// (one day with optional clock_in/clock_out objects) and returns true if it
// should be visible under the currently-active filter.
const FILTERS = {
  all: () => true,
  manual: (e) => !!e.clock_in?.manual_clock_in || !!e.clock_in?.allocated_by,
  missingGps: (e) => {
    const i = e.clock_in;
    const o = e.clock_out;
    const hasIn  = i && (typeof i.latitude === 'number' && typeof i.longitude === 'number');
    const hasOut = o && (typeof o.latitude === 'number' && typeof o.longitude === 'number');
    // missing only if the event exists but has no coords
    if (i && !hasIn) return true;
    if (o && !hasOut) return true;
    return false;
  },
  autoOut: (e) => !!e.clock_out?.auto_clock_out,
  edited: (e) => !!e.clock_in?.edited_by_admin || !!e.clock_out?.edited_by_admin,
};

export default function ClockHistoryPage({ category = null, payrollMode = false, combined = false }) {
  const Layout = payrollMode ? PayrollLayout : AdminLayout;
  const { user: authUser } = useAuth();
  const isPrimaryAdmin = authUser?.role === 'admin';
  const [employees, setEmployees] = useState([]);
  const [loadingEmployees, setLoadingEmployees] = useState(true);
  const [activeEmployees, setActiveEmployees] = useState([]); // worked-this-month roster
  const [activeLoading, setActiveLoading] = useState(false);

  // Default to LAST MONTH during the first 10 days of the current month —
  // that's typically when admin runs payroll for the previous period. Past
  // day 10, default to the current month so the page is also useful mid-cycle.
  const _today = new Date();
  const _useLastMonth = _today.getDate() <= 10;
  const _defaultDate = new Date(_today.getFullYear(), _today.getMonth() - (_useLastMonth ? 1 : 0), 1);
  const [selectedYear, setSelectedYear] = useState(String(_defaultDate.getFullYear()));
  const [selectedMonth, setSelectedMonth] = useState(String(_defaultDate.getMonth() + 1).padStart(2, '0'));

  // The currently "loaded" employee for the View Details / Export operations.
  // Excel/PDF export functions read selectedEmployeeId + viewData unchanged.
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [viewData, setViewData] = useState(null);
  // Ref mirror of the loaded history — exporters read this so the per-card
  // Export buttons work immediately (no stale-state race after loadAndSelect).
  const exportRef = useRef({ data: null, employee: null });
  const [loadingViewId, setLoadingViewId] = useState(null);

  // Cache full history per employee per month so toggling Details / running
  // an export doesn't refetch every time. Keyed by `${id}|${year}-${month}`.
  const [historyCache, setHistoryCache] = useState({});

  // UI state
  const [expandedId, setExpandedId] = useState(null);
  const [search, setSearch] = useState('');
  const [dayDate, setDayDate] = useState('');
  const [dayEvents, setDayEvents] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);
  const [filter, setFilter] = useState('all');

  // Edit / Delete dialogs (kept identical to original)
  const [editingEvent, setEditingEvent] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editTime, setEditTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, event: null });
  const [deleting, setDeleting] = useState(false);
  const [generatingZip, setGeneratingZip] = useState(false);
  const [workHoursFor, setWorkHoursFor] = useState(null);
  const [staffInfoFor, setStaffInfoFor] = useState(null);

  // Year options
  const currentYear = new Date().getFullYear();
  const yearOptions = [];
  for (let y = currentYear; y >= currentYear - 3; y--) yearOptions.push(String(y));

  useEffect(() => { fetchEmployees(); }, []);

  // Reload the active roster whenever the month/year selector changes.
  useEffect(() => {
    const monthParam = `${selectedYear}-${selectedMonth}`;
    let cancelled = false;
    setActiveLoading(true);
    (async () => {
      try {
        const res = await axios.get(`${API}/admin/clock/active-employees?month=${monthParam}`);
        if (cancelled) return;
        setActiveEmployees(res.data.employees || []);
      } catch (e) {
        if (!cancelled) { console.error('active-employees fetch failed', e); setActiveEmployees([]); }
      } finally {
        if (!cancelled) setActiveLoading(false);
      }
    })();
    // Reset transient view state when month flips
    setExpandedId(null);
    setSelectedEmployeeId('');
    setViewData(null);
    return () => { cancelled = true; };
  }, [selectedYear, selectedMonth]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/admin/clock/all-employees`);
      setEmployees(response.data.employees || []);
    } catch (error) {
      toast.error('Failed to load employees');
    } finally {
      setLoadingEmployees(false);
    }
  };

  const selectedEmployee = employees.find((e) => e.id === selectedEmployeeId);
  const monthLabel = (MONTHS[parseInt(selectedMonth, 10) - 1] || {}).label || '';

  // Resolve full-month history for a given employee (memoized in cache).
  const loadEmployeeHistory = async (employeeId, force = false) => {
    const key = `${employeeId}|${selectedYear}-${selectedMonth}`;
    if (!force && historyCache[key]) return historyCache[key];
    if (!force) setLoadingViewId(employeeId);
    try {
      const res = await axios.get(
        `${API}/admin/clock/employee-history/${employeeId}?month=${selectedYear}-${selectedMonth}`
      );
      const history = (res.data.history || []).slice().sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      setHistoryCache((prev) => ({ ...prev, [key]: history }));
      return history;
    } catch (error) {
      toast.error(`Failed to load history: ${error.response?.data?.detail || error.message}`);
      return [];
    } finally {
      if (!force) setLoadingViewId(null);
    }
  };

  // Loads history and keeps selectedEmployeeId/viewData in sync for the
  // Excel/PDF exporters to consume — that contract is unchanged.
  const loadAndSelect = async (employeeId) => {
    const history = await loadEmployeeHistory(employeeId);
    setSelectedEmployeeId(employeeId);
    setViewData(history);
    exportRef.current = { data: history, employee: employees.find((e) => e.id === employeeId) || null };
    return history;
  };

  const handleToggleDetails = async (employeeId) => {
    if (expandedId === employeeId) {
      setExpandedId(null);
      return;
    }
    await loadAndSelect(employeeId);
    setExpandedId(employeeId);
  };

  // Compute per-employee stats from cached history (when available).
  const statsFor = (employeeId) => {
    const key = `${employeeId}|${selectedYear}-${selectedMonth}`;
    const history = historyCache[key];
    if (!history) return null;
    let totalHours = 0;
    let shifts = 0;
    let manual = 0;
    let autoOuts = 0;
    let missingGps = 0;
    let edited = 0;
    history.forEach((e) => {
      if (e.clock_in && e.clock_out) {
        if (typeof e.net_hours === 'number') {
          // Central backend calculation: NET after break deduction.
          totalHours += e.net_hours;
          shifts += 1;
        } else {
          const h = (new Date(e.clock_out.timestamp) - new Date(e.clock_in.timestamp)) / 3600000;
          // Skip implausible pairs (negative or >24h — clock-out from another day)
          if (h >= 0 && h <= 24) {
            totalHours += h;
            shifts += 1;
          }
        }
      }
      if (e.clock_in?.manual_clock_in || e.clock_in?.allocated_by) manual += 1;
      if (e.clock_out?.auto_clock_out) autoOuts += 1;
      if (e.clock_in?.edited_by_admin) edited += 1;
      if (e.clock_out?.edited_by_admin) edited += 1;
      // missing GPS: an event exists but no coords
      const inMissing = e.clock_in && !(typeof e.clock_in.latitude === 'number' && typeof e.clock_in.longitude === 'number');
      const outMissing = e.clock_out && !(typeof e.clock_out.latitude === 'number' && typeof e.clock_out.longitude === 'number');
      if (inMissing) missingGps += 1;
      if (outMissing) missingGps += 1;
    });
    return { totalHours, shifts, manual, autoOuts, missingGps, edited };
  };

  // Visible roster — filter by search text and (optionally) by stat flags.
  // Stat-based filters require the full history to be present in cache; for
  // not-yet-loaded employees we conservatively show them so the admin can
  // click into one without it disappearing pre-load. After expand it'll be
  // re-filtered with real numbers.
  // Show every employee with ANY activity in the selected month — that is,
  // everyone returned by `/admin/clock/active-employees`. The backend already
  // unions clock_events + shifts + open_shifts.claimed_by_list, so this set
  // is the exact intersection of "Shift Calendar" + "Clock History" for the
  // month. We deliberately do NOT hide rows with `worked_hours == 0` here:
  // those represent staff who were scheduled but haven't clocked in yet, and
  // admin needs to spot them so they can be corrected/manual-clocked-in.
  // The card itself colour-codes a 0-hours row so it stands out for action.
  const workedThisMonth = useMemo(() => activeEmployees, [activeEmployees]);

  // ---------- Category pages (Retail / Event Clock History) ----------
  // Site totals grouped by site with scheduled vs worked hours; also gives
  // the set of employees active in this category for roster filtering.
  const [siteTotals, setSiteTotals] = useState(null);
  const [siteTotalsLoading, setSiteTotalsLoading] = useState(false);
  const fetchSiteTotals = useCallback(() => {
    if (!category) return;
    setSiteTotalsLoading(true);
    axios.get(`${API}/admin/clock/site-totals`, {
      params: { month: `${selectedYear}-${selectedMonth}`, category },
    })
      .then((r) => setSiteTotals(r.data))
      .catch((err) => setSiteTotals({
        sites: [], employees: [],
        error: `HTTP ${err?.response?.status || 'network'} — ${err?.response?.data?.detail || err.message}`,
      }))
      .finally(() => setSiteTotalsLoading(false));
  }, [category, selectedYear, selectedMonth]);
  useEffect(() => { fetchSiteTotals(); }, [fetchSiteTotals]);

  // Instant sync: refresh roster + expanded employee on any clock mutation.
  useAutoRefresh(async () => {
    try {
      const res = await axios.get(`${API}/admin/clock/active-employees?month=${selectedYear}-${selectedMonth}`);
      setActiveEmployees(res.data.employees || []);
    } catch {}
    fetchSiteTotals();
    if (expandedId) {
      const history = await loadEmployeeHistory(expandedId, true);
      setViewData(history);
      exportRef.current = { data: history, employee: employees.find((e) => e.id === expandedId) || exportRef.current.employee };
    }
  }, { match: ['/clock', '/shift', '/rota'] });

  const categoryEmployeeIds = useMemo(() => {
    if (!category || !siteTotals) return null;
    // If the aggregation failed, don't hide the roster — keep search usable.
    if (siteTotals.error) return null;
    return new Set((siteTotals.employees || []).map((e) => e.id));
  }, [category, siteTotals]);

  const openEmployeeFromSite = (empId) => {
    if (expandedId !== empId) handleToggleDetails(empId);
    setTimeout(() => {
      document.getElementById(`emp-card-${empId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 150);
  };

  // Visible roster — narrowed only by the optional search text and the
  // optional flag filter chips. When `search` is empty we always show the
  // full worked-this-month list immediately (no extra action required from
  // admin). Filtering by Manual/Auto/MissingGPS/Edited needs the per-
  // employee history to be loaded; until it is we keep the row visible so
  // admin can click into it without it vanishing mid-load.
  const visibleRoster = useMemo(() => {
    const s = search.trim().toLowerCase();
    let list = workedThisMonth;
    // Category pages only show staff scheduled/worked in that category.
    if (categoryEmployeeIds) list = list.filter((emp) => categoryEmployeeIds.has(emp.id));
    if (s) {
      // Comma-separated terms allow searching a GROUP of employees at once.
      const terms = s.split(',').map((t) => t.trim()).filter(Boolean);
      list = list.filter((emp) => terms.some((t) => (emp.name || '').toLowerCase().includes(t)));
    }
    if (filter === 'all') return list;
    return list.filter((emp) => {
      const st = statsFor(emp.id);
      if (!st) return true; // keep visible until history loads
      if (filter === 'manual')     return st.manual > 0;
      if (filter === 'missingGps') return st.missingGps > 0;
      if (filter === 'autoOut')    return st.autoOuts > 0;
      if (filter === 'edited')     return st.edited > 0;
      return true;
    });
  }, [workedThisMonth, search, filter, historyCache, selectedYear, selectedMonth, categoryEmployeeIds]);

  // ---------- Day search (specific date, one employee or a group) ----------
  useEffect(() => {
    if (!dayDate) { setDayEvents([]); return; }
    let mounted = true;
    setDayLoading(true);
    axios.get(`${API}/admin/clock/events-by-date`, { params: { date: dayDate } })
      .then((r) => { if (mounted) setDayEvents(r.data || []); })
      .catch(() => { if (mounted) setDayEvents([]); })
      .finally(() => { if (mounted) setDayLoading(false); });
    return () => { mounted = false; };
  }, [dayDate]);

  const dayResults = useMemo(() => {
    if (!dayDate) return [];
    const terms = search.trim().toLowerCase().split(',').map((t) => t.trim()).filter(Boolean);
    const byEmp = new Map();
    for (const ev of dayEvents) {
      const name = ev.employee_name || '';
      if (terms.length && !terms.some((t) => name.toLowerCase().includes(t))) continue;
      if (!byEmp.has(ev.employee_id)) byEmp.set(ev.employee_id, { id: ev.employee_id, name, events: [] });
      byEmp.get(ev.employee_id).events.push(ev);
    }
    const rows = [];
    for (const emp of byEmp.values()) {
      const evs = emp.events.slice().sort((a, b) => (a.timestamp || '').localeCompare(b.timestamp || ''));
      const pairs = [];
      let openIn = null;
      for (const ev of evs) {
        if (ev.event_type === 'clock_in') {
          if (openIn) pairs.push({ in: openIn, out: null });
          openIn = ev;
        } else if (ev.event_type === 'clock_out') {
          pairs.push({ in: openIn, out: ev });
          openIn = null;
        }
      }
      if (openIn) pairs.push({ in: openIn, out: null });
      rows.push({ ...emp, pairs });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name));
  }, [dayDate, dayEvents, search]);

  // ---------- Edit handlers ----------
  const handleEditClick = (event) => {
    setEditDate(formatUK(event.timestamp, 'yyyy-MM-dd'));
    setEditTime(formatUK(event.timestamp, 'HH:mm'));
    setEditingEvent(event);
  };

  const handleSaveEdit = async () => {
    if (!editDate || !editTime) { toast.error('Please select date and time'); return; }
    setSaving(true);
    try {
      // Send the typed wall-clock time as UK-naive (backend treats it as UK
      // time). The old `...Z` form declared it UTC, silently shifting saved
      // times by +1h during British Summer Time.
      const newTimestamp = `${editDate}T${editTime}:00`;
      await axios.put(`${API}/admin/clock/edit/${editingEvent.id}`, { new_timestamp: newTimestamp });
      toast.success('Time updated');
      setEditingEvent(null);
      // Invalidate cache + reload the affected employee
      const key = `${selectedEmployeeId}|${selectedYear}-${selectedMonth}`;
      setHistoryCache((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
      if (selectedEmployeeId) await loadAndSelect(selectedEmployeeId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => { setEditingEvent(null); setEditDate(''); setEditTime(''); };

  // ---------- Break override (Primary Admin only) ----------
  const [breakDialog, setBreakDialog] = useState(null); // { entry, employeeId }
  const [breakMinutesSel, setBreakMinutesSel] = useState('');
  const [breakCustom, setBreakCustom] = useState('');
  const [breakReason, setBreakReason] = useState('');
  const [breakSaving, setBreakSaving] = useState(false);

  const openBreakDialog = (entry, employeeId) => {
    const current = entry.break_minutes;
    setBreakMinutesSel([0, 20, 30, 45, 60].includes(current) ? String(current) : 'custom');
    setBreakCustom([0, 20, 30, 45, 60].includes(current) ? '' : String(current ?? ''));
    setBreakReason('');
    setBreakDialog({ entry, employeeId });
  };

  const refreshAfterBreakChange = async (employeeId) => {
    const key = `${employeeId}|${selectedYear}-${selectedMonth}`;
    setHistoryCache((prev) => { const c = { ...prev }; delete c[key]; return c; });
    await loadAndSelect(employeeId);
  };

  const handleSaveBreak = async () => {
    const mins = breakMinutesSel === 'custom' ? parseInt(breakCustom, 10) : parseInt(breakMinutesSel, 10);
    if (Number.isNaN(mins) || mins < 0 || mins > 480) { toast.error('Select a valid break duration (0–480 min)'); return; }
    if (!breakReason.trim()) { toast.error('A reason is mandatory for a break override'); return; }
    setBreakSaving(true);
    try {
      await axios.post(`${API}/admin/clock/break-override`, {
        employee_id: breakDialog.employeeId,
        clock_in_event_id: breakDialog.entry.clock_in.id,
        break_minutes: mins,
        reason: breakReason.trim(),
        auto_break_minutes: breakDialog.entry.auto_break_minutes ?? -1,
      });
      toast.success('Break override saved');
      const empId = breakDialog.employeeId;
      setBreakDialog(null);
      await refreshAfterBreakChange(empId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save break override');
    } finally { setBreakSaving(false); }
  };

  const handleResetBreak = async () => {
    if (!breakReason.trim()) { toast.error('A reason is mandatory to restore the automatic break'); return; }
    setBreakSaving(true);
    try {
      await axios.delete(`${API}/admin/clock/break-override/${breakDialog.entry.clock_in.id}`, {
        params: { reason: breakReason.trim() },
      });
      toast.success('Automatic break restored');
      const empId = breakDialog.employeeId;
      setBreakDialog(null);
      await refreshAfterBreakChange(empId);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore automatic break');
    } finally { setBreakSaving(false); }
  };

  const handleDeleteClick = (event) => setDeleteDialog({ open: true, event });

  const handleConfirmDelete = async () => {
    if (!deleteDialog.event) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/clock/${deleteDialog.event.id}`);
      toast.success('Event deleted');
      setDeleteDialog({ open: false, event: null });
      const key = `${selectedEmployeeId}|${selectedYear}-${selectedMonth}`;
      setHistoryCache((prev) => { const copy = { ...prev }; delete copy[key]; return copy; });
      if (selectedEmployeeId) await loadAndSelect(selectedEmployeeId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  // ---------- Print PDF ----------
  // The report window MUST be opened synchronously on the user's tap —
  // opening it after async work gets popup-blocked on mobile browsers.
  const REPORT_LOADING_HTML = '<html><head><title>Clock Report</title></head><body style="font-family:sans-serif;padding:48px 24px;text-align:center;color:#52525b;"><h3 style="margin-bottom:8px;">Generating report…</h3><p style="font-size:14px;">Please wait — fetching clock data and photos.</p></body></html>';

  const handlePrintReport = async (preOpenedWin = null) => {
    // Shadow state with the ref mirror — always the freshest loaded history.
    const viewData = exportRef.current.data;
    const selectedEmployee = exportRef.current.employee;
    if (!viewData || viewData.length === 0 || !selectedEmployee) {
      if (preOpenedWin && !preOpenedWin.closed) preOpenedWin.close();
      toast.error('No data to print. Open Details first.');
      return;
    }
    let printWindow = preOpenedWin;
    if (!printWindow) {
      printWindow = window.open('', '_blank');
      if (printWindow) printWindow.document.write(REPORT_LOADING_HTML);
    }
    toast.info('Generating report — fetching photos...');

    const photoIds = [];
    viewData.forEach((entry) => {
      if (entry.clock_in?.photo_url === 'has_photo' && entry.clock_in?.id) photoIds.push(entry.clock_in.id);
      if (entry.clock_out?.photo_url === 'has_photo' && entry.clock_out?.id) photoIds.push(entry.clock_out.id);
    });
    const photoMap = {};
    if (photoIds.length > 0) {
      try {
        // One batched request instead of one HTTP call per photo — much
        // faster on months with many punches.
        const r = await axios.post(`${API}/admin/clock/photos/batch`, { ids: photoIds });
        Object.assign(photoMap, r.data?.photos || {});
      } catch (_e) {
        // Fallback: per-photo fetch (older backend without the batch route)
        await Promise.all(photoIds.map(async (id) => {
          try {
            const r = await axios.get(`${API}/admin/clock/photo/${id}`);
            if (r.data?.photo_url && r.data.photo_url !== 'has_photo') photoMap[id] = r.data.photo_url;
          } catch (_e2) { /* swallow */ }
        }));
      }
    }

    let totalHours = 0;
    let totalGrossPdf = 0;
    let totalBreakMinPdf = 0;
    let daysWorked = 0;
    const hoursByRole = {};
    const scheduledHoursByRole = {};
    // Same robust HH:MM parser as the Excel export — tolerates whitespace,
    // seconds suffix, dot separator, and 12h suffix.
    const parseHHMM = (t) => {
      if (t === null || t === undefined) return null;
      const s = String(t).trim();
      if (!s) return null;
      const m = /^(\d{1,2})[:.](\d{2})/.exec(s);
      if (!m) return null;
      const h = parseInt(m[1], 10);
      const mm = parseInt(m[2], 10);
      if (Number.isNaN(h) || Number.isNaN(mm)) return null;
      if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
      return { h, m: mm };
    };
    // Worked-hours helper: guards against invalid timestamps / negative
    // durations / >24h cross-day clock-outs. Returns null when the row must
    // be skipped so the PDF total isn't poisoned by NaN.
    const computeWorkedHours = (ciTs, coTs) => {
      if (!ciTs || !coTs) return null;
      const inMs = new Date(ciTs).getTime();
      const outMs = new Date(coTs).getTime();
      if (Number.isNaN(inMs) || Number.isNaN(outMs)) return null;
      const diff = (outMs - inMs) / 3600000;
      if (diff < 0 || diff > 24) return null;
      return diff;
    };
    viewData.forEach((entry) => {
      if (entry.clock_in && entry.clock_out) {
        const diff = computeWorkedHours(entry.clock_in.timestamp, entry.clock_out.timestamp);
        if (diff !== null) {
          const netVal = typeof entry.net_hours === 'number' ? entry.net_hours : diff;
          totalHours += netVal;
          totalGrossPdf += typeof entry.gross_hours === 'number' ? entry.gross_hours : diff;
          totalBreakMinPdf += typeof entry.break_minutes === 'number' ? entry.break_minutes : 0;
          daysWorked++;
          const roleKey = entry.clock_in?.shift_role || entry.shift_role || 'Unspecified';
          hoursByRole[roleKey] = (hoursByRole[roleKey] || 0) + netVal;
        }
      }
      const s = parseHHMM(entry.shift_start);
      const e = parseHHMM(entry.shift_end);
      if (s && e) {
        let mins = (e.h * 60 + e.m) - (s.h * 60 + s.m);
        if (mins <= 0) mins += 24 * 60;
        const schedRoleKey = entry.shift_role || entry.clock_in?.shift_role || 'Unspecified';
        scheduledHoursByRole[schedRoleKey] = (scheduledHoursByRole[schedRoleKey] || 0) + mins / 60;
      }
    });
    const roleBreakdownRows = Object.entries(hoursByRole)
      .sort((a, b) => b[1] - a[1])
      .map(([role, hrs]) => `<tr><td class="label-col">${role}</td><td class="value-col">${hrs.toFixed(2)} hrs</td></tr>`)
      .join('');
    const scheduledBreakdownRows = Object.entries(scheduledHoursByRole)
      .sort((a, b) => b[1] - a[1])
      .map(([role, hrs]) => `<tr><td class="label-col">${role}</td><td class="value-col">${hrs.toFixed(2)} hrs</td></tr>`)
      .join('');
    const totalScheduledHours = Object.values(scheduledHoursByRole).reduce((s, h) => s + h, 0);

    // Auto-verification against independently recomputed punches
    let verifyDaysPdf = {};
    try {
      const vr = await axios.get(`${API}/admin/clock/verify/${selectedEmployee.id}`, { params: { month: `${selectedYear}-${selectedMonth}` } });
      verifyDaysPdf = vr.data?.days || {};
    } catch (_e) { /* n/a */ }
    let pdfVerifyOk = 0; let pdfVerifyDiff = 0;

    const entriesHtml = viewData.map((entry) => {
      const clockIn = entry.clock_in ? formatUK(entry.clock_in.timestamp) : '--';
      const clockOut = entry.clock_out ? formatUK(entry.clock_out.timestamp) : '--';
      let hours = '--';
      let hoursNum = null;
      if (entry.clock_in && entry.clock_out) {
        const diff = computeWorkedHours(entry.clock_in.timestamp, entry.clock_out.timestamp);
        hoursNum = diff;
        hours = diff !== null ? diff.toFixed(2) + ' hrs' : 'INVALID';
      }
      const hasBreakInfo = typeof entry.net_hours === 'number';
      const hoursCellHtml = hasBreakInfo
        ? `<strong>Gross:</strong> ${entry.gross_hours.toFixed(2)}h&nbsp;·&nbsp;<strong>Break:</strong> ${entry.break_minutes} min${entry.break_overridden ? ' <span style="color:#2563eb;font-weight:700;">(override)</span>' : ''}&nbsp;·&nbsp;<strong style="color:#18181b;">Net: ${entry.net_hours.toFixed(2)}h</strong>`
        : `<strong>Hours:</strong> ${hours}`;
      let verifyBadge = '';
      if (entry.clock_in || entry.clock_out) {
        const v = verifyDaysPdf[entry.date];
        const inHH = entry.clock_in ? clockIn.slice(-5) : '';
        const outHH = entry.clock_out ? clockOut.slice(-5) : '';
        if (v && v.clock_in === inHH && (v.clock_out || '') === outHH
          && (hoursNum === null || Math.abs((v.hours || 0) - hoursNum) < 0.02)) {
          pdfVerifyOk += 1;
          verifyBadge = '<span style="background:#dcfce7;color:#166534;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:600;">✓ PONTAJ VERIFIED</span>';
        } else {
          pdfVerifyDiff += 1;
          const vtxt = v ? `pontaj: ${v.clock_in || '--'}–${v.clock_out || '--'} (${(v.hours || 0).toFixed(2)}h)` : 'no pontaj found';
          verifyBadge = `<span style="background:#fecaca;color:#991b1b;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700;">⚠ CHECK — ${vtxt}</span>`;
        }
      }
      const dateStr = format(parseISO(entry.date), 'EEEE, dd MMMM yyyy');
      const scheduled = entry.shift_start && entry.shift_end ? `${entry.shift_start} - ${entry.shift_end}` : '';
      const location = entry.site_name || '';
      const role = entry.clock_in?.shift_role || '';
      const lateInReason = entry.clock_in?.late_reason || (entry.clock_in?.notes?.includes('Late') ? entry.clock_in.notes : '');
      const lateOutReason = entry.clock_out?.late_reason || (entry.clock_out?.notes?.includes('Late') ? entry.clock_out.notes : '');
      const lateBlocks = [];
      if (lateInReason) lateBlocks.push(`<div style="background:#fef3c7;padding:6px 10px;border-left:3px solid #f59e0b;margin:6px 0;font-size:11px;"><strong>Late Clock-In Reason:</strong> ${lateInReason}</div>`);
      if (lateOutReason) lateBlocks.push(`<div style="background:#ffedd5;padding:6px 10px;border-left:3px solid #ea580c;margin:6px 0;font-size:11px;"><strong>Late Clock-Out Reason:</strong> ${lateOutReason}</div>`);
      const lateInfo = lateBlocks.join('');
      const clockInPhoto = entry.clock_in?.id && photoMap[entry.clock_in.id];
      const clockOutPhoto = entry.clock_out?.id && photoMap[entry.clock_out.id];
      const photosRow = (clockInPhoto || clockOutPhoto) ? `<div style="display:flex;gap:12px;margin-top:10px;page-break-inside:avoid;">
             ${clockInPhoto ? `<div style="flex:1;text-align:center;"><div style="font-size:10px;color:#16a34a;font-weight:600;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">Clock In Photo</div><img src="${clockInPhoto}" style="max-width:100%;max-height:180px;border:1px solid #e5e7eb;border-radius:4px;object-fit:contain;" /></div>` : `<div style="flex:1;"></div>`}
             ${clockOutPhoto ? `<div style="flex:1;text-align:center;"><div style="font-size:10px;color:#dc2626;font-weight:600;text-transform:uppercase;letter-spacing:1px;margin-bottom:4px;">Clock Out Photo</div><img src="${clockOutPhoto}" style="max-width:100%;max-height:180px;border:1px solid #e5e7eb;border-radius:4px;object-fit:contain;" /></div>` : `<div style="flex:1;"></div>`}
           </div>` : '';
      return `
        <div style="border:1px solid #e5e7eb;border-radius:6px;padding:14px;margin-bottom:12px;page-break-inside:avoid;">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:8px;">
            <div>
              <strong style="font-size:14px;">${dateStr}</strong>
              ${scheduled ? `<span style="color:#6b7280;font-size:12px;margin-left:12px;">Scheduled: ${scheduled}</span>` : ''}
            </div>
            <div style="display:flex;gap:8px;align-items:center;">
              ${verifyBadge}
              ${role ? `<span style="background:#dbeafe;padding:2px 8px;border-radius:4px;font-size:11px;color:#1e40af;">${role}</span>` : ''}
              ${location ? `<span style="background:#f3f4f6;padding:2px 8px;border-radius:4px;font-size:11px;color:#4b5563;">${location}</span>` : ''}
            </div>
          </div>
          <table style="width:100%;font-size:13px;">
            <tr>
              <td style="padding:4px 0;"><span style="color:#16a34a;font-weight:600;">Clock In:</span> ${clockIn}</td>
              <td style="padding:4px 0;"><span style="color:#dc2626;font-weight:600;">Clock Out:</span> ${clockOut}</td>
              <td style="padding:4px 0;">${hoursCellHtml}</td>
            </tr>
          </table>
          ${lateInfo}
          ${photosRow}
        </div>`;
    }).join('');

    const html = `<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>San Security - ${selectedEmployee.name} - ${monthLabel} ${selectedYear}</title>
<style>
  @media print { body { margin: 0; } .no-print { display: none !important; } @page { margin: 15mm; } }
  body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #111; background: #fff; padding: 20px; }
  .header { text-align: center; border-bottom: 3px solid #FACC15; padding-bottom: 16px; margin-bottom: 20px; }
  .header h1 { color: #FACC15; font-size: 28px; letter-spacing: 3px; margin: 0; background: #18181b; padding: 12px; }
  .header h2 { margin: 10px 0 4px; font-size: 18px; }
  .header p { margin: 2px 0; color: #6b7280; font-size: 13px; }
  .summary { display: flex; gap: 20px; justify-content: center; margin: 16px 0 24px; }
  .summary-box { background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 6px; padding: 12px 24px; text-align: center; }
  .summary-box .num { font-size: 22px; font-weight: 700; color: #18181b; }
  .summary-box .label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 1px; }
  .print-btn { position: fixed; top: 20px; right: 20px; background: #FACC15; color: #18181b; border: none; padding: 10px 24px; font-size: 14px; font-weight: 600; border-radius: 6px; cursor: pointer; z-index:100; }
  .print-btn:hover { background: #eab308; }
  .footer { text-align: center; color: #9ca3af; font-size: 11px; margin-top: 24px; padding-top: 12px; border-top: 1px solid #e5e7eb; }
  .totals-section { margin-top: 30px; border: 2px solid #18181b; border-radius: 8px; padding: 20px; page-break-inside: avoid; }
  .totals-section h3 { margin: 0 0 14px 0; font-size: 16px; text-transform: uppercase; letter-spacing: 2px; border-bottom: 2px solid #FACC15; padding-bottom: 8px; }
  .totals-table { width: 100%; border-collapse: collapse; font-size: 13px; }
  .totals-table td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; }
  .totals-table tr:last-child td { border-bottom: none; }
  .totals-table .label-col { color: #6b7280; width: 60%; }
  .totals-table .value-col { text-align: right; font-weight: 700; font-size: 15px; }
  .totals-highlight { background: #FACC15; color: #18181b; padding: 12px 20px; border-radius: 6px; text-align: center; margin-top: 14px; }
  .totals-highlight .big { font-size: 28px; font-weight: 800; }
  .totals-highlight .small { font-size: 12px; text-transform: uppercase; letter-spacing: 1px; }
</style></head><body>
<button class="print-btn no-print" onclick="window.print()">Print / Save PDF</button>
<div class="header">
  <h1>SAN SECURITY</h1>
  <h2>Monthly Clock Report</h2>
  <p><strong>Employee:</strong> ${selectedEmployee.name}</p>
  <p><strong>Email:</strong> ${selectedEmployee.email}</p>
  <p><strong>Month:</strong> ${monthLabel} ${selectedYear}</p>
</div>
<div class="summary">
  <div class="summary-box"><div class="num">${viewData.length}</div><div class="label">Total Days</div></div>
  <div class="summary-box"><div class="num">${daysWorked}</div><div class="label">Days Worked</div></div>
  <div class="summary-box"><div class="num">${totalGrossPdf.toFixed(2)}</div><div class="label">Gross Hours</div></div>
  <div class="summary-box"><div class="num">${Math.floor(totalBreakMinPdf / 60)}h ${totalBreakMinPdf % 60}m</div><div class="label">Break Time</div></div>
  <div class="summary-box"><div class="num">${totalHours.toFixed(2)}</div><div class="label">Net Worked Hours</div></div>
</div>
${entriesHtml}
<div style="margin-top:14px;padding:10px 14px;border-radius:6px;font-weight:700;font-size:12px;background:${pdfVerifyDiff > 0 ? '#fecaca' : '#dcfce7'};color:${pdfVerifyDiff > 0 ? '#991b1b' : '#166534'};">
AUTO-VERIFICATION: ${pdfVerifyOk + pdfVerifyDiff} rows checked against recorded punches · ${pdfVerifyOk} verified · ${pdfVerifyDiff} flagged
</div>
<div class="totals-section">
  <h3>Total Work Hours</h3>
  <table class="totals-table">
    <tr><td class="label-col">Total Scheduled Days</td><td class="value-col">${viewData.length}</td></tr>
    <tr><td class="label-col">Days Clocked In</td><td class="value-col">${daysWorked}</td></tr>
    <tr><td class="label-col">Days Not Clocked In</td><td class="value-col">${viewData.length - daysWorked}</td></tr>
    <tr><td class="label-col">Total Gross Hours</td><td class="value-col">${totalGrossPdf.toFixed(2)} hrs</td></tr>
    <tr><td class="label-col">Total Break Time</td><td class="value-col">${Math.floor(totalBreakMinPdf / 60)}h ${totalBreakMinPdf % 60}m</td></tr>
    <tr><td class="label-col">Total Net Worked Hours</td><td class="value-col">${totalHours.toFixed(2)} hrs</td></tr>
    <tr><td class="label-col">Average Net Hours Per Day</td><td class="value-col">${daysWorked > 0 ? (totalHours / daysWorked).toFixed(2) : '0.00'} hrs</td></tr>
  </table>
  ${roleBreakdownRows ? `
  <h3 style="margin-top:20px;">Hours by Role (Worked) — ${selectedEmployee.name}</h3>
  <table class="totals-table">
    ${roleBreakdownRows}
  </table>` : ''}
  ${scheduledBreakdownRows ? `
  <h3 style="margin-top:20px;">Hours by Role (Scheduled) — ${selectedEmployee.name}</h3>
  <table class="totals-table">
    ${scheduledBreakdownRows}
    <tr><td class="label-col"><strong>Total Scheduled</strong></td><td class="value-col"><strong>${totalScheduledHours.toFixed(2)} hrs</strong></td></tr>
  </table>` : ''}
  <div class="totals-highlight">
    <div class="big">${totalHours.toFixed(2)}</div>
    <div class="small">Total Net Worked Hours (after breaks)</div>
  </div>
</div>
<div class="footer">Generated: ${format(new Date(), 'dd/MM/yyyy HH:mm')} — San Security</div>
</body></html>`;

    if (printWindow && !printWindow.closed) {
      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();
    } else {
      // Popup was blocked — offer a tap-to-open fallback (runs on user gesture, never blocked).
      const blobUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
      toast.info('Report ready', {
        description: 'Your browser blocked the popup. Tap OPEN to view the report.',
        duration: 20000,
        action: { label: 'OPEN', onClick: () => window.open(blobUrl, '_blank') },
      });
    }
  };

  // ---------- Excel Download ---------- (unchanged behaviour from prior version) ----------
  const handleDownloadExcel = async () => {
    // Shadow state with the ref mirror — always the freshest loaded history.
    const viewData = exportRef.current.data;
    const selectedEmployee = exportRef.current.employee;
    if (!viewData || viewData.length === 0 || !selectedEmployee) {
      toast.error('No data to download. Open Details first.');
      return;
    }
    setGeneratingZip(true);
    try {
      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'San Security';
      const worksheet = workbook.addWorksheet('Clock Report');

      worksheet.mergeCells('A1:T1');
      worksheet.getCell('A1').value = `SAN SECURITY - ${selectedEmployee.name} - ${monthLabel} ${selectedYear}`;
      worksheet.getCell('A1').font = { size: 14, bold: true };
      worksheet.getCell('A1').alignment = { horizontal: 'center' };
      worksheet.addRow([]);

      const headerRow = worksheet.addRow(['Date', 'Day', 'Scheduled', 'Clock In', 'Clock Out', 'Gross Hours', 'Break', 'Net Worked Hours', 'Location', 'Role', 'Late In Reason', 'Late Out Reason', 'Notes', 'Clock In Photo', 'Clock Out Photo', 'Clock In GPS', 'Clock Out GPS', 'Pontaj Check', 'Rate (£)', 'Pay (£)']);
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF18181b' } };
      headerRow.alignment = { horizontal: 'center' };

      worksheet.columns = [
        { width: 14 }, { width: 12 }, { width: 14 },
        { width: 10 }, { width: 10 }, { width: 11 }, { width: 11 }, { width: 13 },
        { width: 22 }, { width: 22 },
        { width: 30 }, { width: 30 },
        { width: 30 },
        { width: 22 }, { width: 22 },
        { width: 16 }, { width: 16 },
        { width: 34 },
        { width: 10 }, { width: 12 },
      ];

      let totalHours = 0;
      let totalGross = 0;
      let totalBreakMin = 0;
      let shiftsWorkedCount = 0;
      let totalPay = 0;
      const hoursByRole = {};
      const scheduledHoursByRole = {};
      // Track scheduled-days that were skipped because their start/end couldn't
      // be parsed. These are the #1 source of "why doesn't 20 × 12 = 240?"
      // discrepancies (empty times, wrong format, whitespace, etc.). We surface
      // the count at the bottom of the Excel so admins can spot it instantly.
      const skippedScheduleRows = [];
      // Robust HH:MM parser — tolerates leading/trailing whitespace, seconds
      // suffix ("10:00:00"), dot separator ("10.00") and 12h suffixes like
      // "10:00 AM". Returns null when nothing digit-like is present.
      const parseHHMM = (t) => {
        if (t === null || t === undefined) return null;
        const s = String(t).trim();
        if (!s) return null;
        const m = /^(\d{1,2})[:.](\d{2})/.exec(s);
        if (!m) return null;
        const h = parseInt(m[1], 10);
        const mm = parseInt(m[2], 10);
        if (Number.isNaN(h) || Number.isNaN(mm)) return null;
        // Clamp obviously-broken values so downstream math never returns NaN.
        if (h < 0 || h > 23 || mm < 0 || mm > 59) return null;
        return { h, m: mm };
      };

      // Worked-hours helper: guards against invalid timestamps (Invalid Date
      // silently coerces to NaN and poisons the running total), negative
      // durations (clock_out earlier than clock_in — bad data), and absurd
      // durations (>48h — usually a stale clock_in that never got closed).
      // Returns { hours: number, warning?: string } or { hours: null, warning }
      // when the row must be skipped. This does NOT touch clock in/out logic.
      const computeWorkedHours = (ciTs, coTs) => {
        if (!ciTs || !coTs) return { hours: null, warning: 'missing timestamp' };
        const inMs = new Date(ciTs).getTime();
        const outMs = new Date(coTs).getTime();
        if (Number.isNaN(inMs) || Number.isNaN(outMs)) {
          return { hours: null, warning: 'invalid timestamp format' };
        }
        const diff = (outMs - inMs) / 3600000;
        if (diff < 0) return { hours: null, warning: 'clock-out before clock-in' };
        if (diff > 24) return { hours: null, warning: `${diff.toFixed(1)}h — clock-out appears to belong to another day, please review` };
        return { hours: diff };
      };
      // Rows dropped from the worked-hours total so admin sees exactly which
      // day is missing and why (mirrors the DATA WARNING for scheduled hours).
      const skippedWorkedRows = [];

      const photoIds = [];
      viewData.forEach((entry) => {
        if (entry.clock_in?.photo_url === 'has_photo' && entry.clock_in?.id) photoIds.push(entry.clock_in.id);
        if (entry.clock_out?.photo_url === 'has_photo' && entry.clock_out?.id) photoIds.push(entry.clock_out.id);
      });
      const photoMap = {};
      if (photoIds.length > 0) {
        try {
          // One batched request instead of one HTTP call per photo — much
          // faster on production months and photos no longer go missing.
          const r = await axios.post(`${API}/admin/clock/photos/batch`, { ids: photoIds });
          Object.assign(photoMap, r.data?.photos || {});
        } catch (_e) {
          // Fallback: per-photo fetch (older backend without the batch route)
          await Promise.all(photoIds.map(async (id) => {
            try {
              const r = await axios.get(`${API}/admin/clock/photo/${id}`);
              if (r.data?.photo_url && r.data.photo_url !== 'has_photo') photoMap[id] = r.data.photo_url;
            } catch (_e2) { /* skip */ }
          }));
        }
      }

      // Auto-verification: independent server recomputation of the month's
      // punches — every written row is checked against it.
      let verifyDays = {};
      try {
        const vr = await axios.get(`${API}/admin/clock/verify/${selectedEmployee.id}`, { params: { month: `${selectedYear}-${selectedMonth}` } });
        verifyDays = vr.data?.days || {};
      } catch (_e) { /* verification unavailable — column shows n/a */ }
      let verifyOk = 0; let verifyDiff = 0;

      for (let i = 0; i < viewData.length; i++) {
        const entry = viewData[i];
        const shiftStarted = (() => {
          const t = parseHHMM(entry.shift_start) || { h: 0, m: 0 };
          const d = parseISO(entry.date);
          d.setHours(t.h, t.m, 0, 0);
          return d.getTime() <= Date.now();
        })();
        const missingPunch = entry.clock_in ? !entry.clock_out : (!!entry.clock_out || shiftStarted);
        const clockIn = entry.clock_in ? formatUK(entry.clock_in.timestamp) : (missingPunch ? 'NO CLOCK IN' : '--');
        const clockOut = entry.clock_out ? formatUK(entry.clock_out.timestamp) : (missingPunch ? 'NO CLOCK OUT' : '--');
        let hours = 0;
        let hoursStr = '--';
        // Central backend values (break deduction applied server-side).
        const grossB = typeof entry.gross_hours === 'number' ? entry.gross_hours : null;
        const brkB = typeof entry.break_minutes === 'number' ? entry.break_minutes : null;
        const netB = typeof entry.net_hours === 'number' ? entry.net_hours : null;
        if (entry.clock_in && entry.clock_out) {
          const worked = computeWorkedHours(entry.clock_in.timestamp, entry.clock_out.timestamp);
          if (worked.hours !== null) {
            hours = worked.hours;
            hoursStr = hours.toFixed(2);
            const netVal = netB !== null ? netB : hours;
            totalHours += netVal;
            totalGross += grossB !== null ? grossB : hours;
            totalBreakMin += brkB !== null ? brkB : 0;
            shiftsWorkedCount += 1;
            const roleKey = entry.clock_in?.shift_role || entry.shift_role || 'Unspecified';
            hoursByRole[roleKey] = (hoursByRole[roleKey] || 0) + netVal;
          } else {
            hoursStr = 'INVALID';
            skippedWorkedRows.push({
              date: entry.date,
              raw_in: entry.clock_in?.timestamp || '',
              raw_out: entry.clock_out?.timestamp || '',
              reason: worked.warning || 'unknown',
            });
          }
        }
        {
          const s = parseHHMM(entry.shift_start);
          const e = parseHHMM(entry.shift_end);
          if (s && e) {
            let mins = (e.h * 60 + e.m) - (s.h * 60 + s.m);
            if (mins <= 0) mins += 24 * 60;
            const schedRoleKey = entry.shift_role || entry.clock_in?.shift_role || 'Unspecified';
            scheduledHoursByRole[schedRoleKey] = (scheduledHoursByRole[schedRoleKey] || 0) + mins / 60;
          } else if (entry.shift_start || entry.shift_end) {
            // Only flag as "skipped" when the row actually pretends to be a
            // scheduled shift (has at least one time). Orphan clock-in rows
            // with both times blank are legitimately zero and should not
            // trigger a warning.
            skippedScheduleRows.push({
              date: entry.date,
              raw_start: entry.shift_start || '',
              raw_end: entry.shift_end || '',
            });
          }
        }

        const clockInPhotoData = entry.clock_in?.id && photoMap[entry.clock_in.id];
        const clockOutPhotoData = entry.clock_out?.id && photoMap[entry.clock_out.id];
        const clockInPhotoCell = clockInPhotoData ? 'See image' : (entry.clock_in?.photo_url === 'has_photo' ? 'Photo in app' : '--');
        const clockOutPhotoCell = clockOutPhotoData ? 'See image' : (entry.clock_out?.photo_url === 'has_photo' ? 'Photo in app' : '--');

        // Google Maps link built from the punch GPS coordinates.
        const gpsLink = (e) => (e && typeof e.latitude === 'number' && typeof e.longitude === 'number')
          ? `https://maps.google.com/?q=${e.latitude},${e.longitude}`
          : null;
        const inGps = gpsLink(entry.clock_in);
        const outGps = gpsLink(entry.clock_out);

        const lateInReason = entry.clock_in?.late_reason || '';
        const lateOutReason = entry.clock_out?.late_reason || '';

        let pontajCheck = 'n/a';
        if (entry.clock_in || entry.clock_out) {
          const v = verifyDays[entry.date];
          const inHH = entry.clock_in ? formatUK(entry.clock_in.timestamp).slice(-5) : '';
          const outHH = entry.clock_out ? formatUK(entry.clock_out.timestamp).slice(-5) : '';
          if (v && v.clock_in === inHH && (v.clock_out || '') === outHH
            && (hoursStr === '--' || Math.abs((v.hours || 0) - hours) < 0.02)) {
            pontajCheck = '✓ verified';
            verifyOk += 1;
          } else if (v) {
            pontajCheck = `⚠ DIFF — pontaj: ${v.clock_in || '--'}–${v.clock_out || '--'} (${(v.hours || 0).toFixed(2)}h)`;
            verifyDiff += 1;
          } else {
            pontajCheck = '⚠ no pontaj found for this day';
            verifyDiff += 1;
          }
        }
        const rate = Number(entry.hourly_rate) || 0;
        const payHours = (typeof entry.net_hours === 'number') ? entry.net_hours : hours;
        const pay = !missingPunch && rate > 0 && typeof payHours === 'number' && payHours > 0 ? rate * payHours : 0;
        if (pay > 0) totalPay += pay;
        const grossCell = (typeof entry.gross_hours === 'number') ? entry.gross_hours.toFixed(2) : hoursStr;
        const breakCell = (typeof entry.break_minutes === 'number')
          ? `${entry.break_minutes} min${entry.break_overridden ? ' *' : ''}`
          : (hoursStr === '--' ? '--' : '');
        const netCell = missingPunch ? 'NOT COUNTED'
          : (typeof entry.net_hours === 'number') ? entry.net_hours.toFixed(2) : (hoursStr === 'INVALID' ? 'INVALID' : '--');
        const row = worksheet.addRow([
          format(parseISO(entry.date), 'dd/MM/yyyy'),
          format(parseISO(entry.date), 'EEEE'),
          entry.shift_start && entry.shift_end ? `${entry.shift_start} - ${entry.shift_end}` : '--',
          clockIn,
          clockOut,
          grossCell,
          breakCell,
          netCell,
          entry.site_name || '--',
          entry.clock_in?.shift_role || '--',
          lateInReason || '--',
          lateOutReason || '--',
          entry.clock_in?.notes || '',
          clockInPhotoCell,
          clockOutPhotoCell,
          inGps ? 'View map' : (entry.clock_in ? 'No GPS' : '--'),
          outGps ? 'View map' : (entry.clock_out ? 'No GPS' : '--'),
          pontajCheck,
          rate > 0 ? rate.toFixed(2) : '--',
          pay > 0 ? pay.toFixed(2) : '--',
        ]);
        if (entry.break_overridden) {
          row.getCell(7).font = { bold: true, color: { argb: 'FF2563EB' } };
        }
        if (pontajCheck.startsWith('⚠')) {
          row.getCell(18).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFECACA' } };
          row.getCell(18).font = { bold: true, color: { argb: 'FF991B1B' } };
        } else if (pontajCheck.startsWith('✓')) {
          row.getCell(18).font = { color: { argb: 'FF16A34A' } };
        }

        if (inGps) {
          const c = row.getCell(16);
          c.value = { text: 'View map', hyperlink: inGps };
          c.font = { color: { argb: 'FF2563EB' }, underline: true };
        }
        if (outGps) {
          const c = row.getCell(17);
          c.value = { text: 'View map', hyperlink: outGps };
          c.font = { color: { argb: 'FF2563EB' }, underline: true };
        }

        const rowIdx = row.number;
        let needsTallRow = false;
        if (clockInPhotoData) {
          try {
            const m = /^data:image\/(\w+);base64,(.*)$/.exec(clockInPhotoData);
            const ext = (m?.[1] || 'jpeg').toLowerCase();
            const b64 = m?.[2] || clockInPhotoData;
            const imgId = workbook.addImage({ base64: b64, extension: ext === 'jpg' ? 'jpeg' : ext });
            worksheet.addImage(imgId, { tl: { col: 13, row: rowIdx - 1 }, ext: { width: 100, height: 75 } });
            needsTallRow = true;
          } catch (_e) { /* keep text only */ }
        }
        if (clockOutPhotoData) {
          try {
            const m = /^data:image\/(\w+);base64,(.*)$/.exec(clockOutPhotoData);
            const ext = (m?.[1] || 'jpeg').toLowerCase();
            const b64 = m?.[2] || clockOutPhotoData;
            const imgId = workbook.addImage({ base64: b64, extension: ext === 'jpg' ? 'jpeg' : ext });
            worksheet.addImage(imgId, { tl: { col: 14, row: rowIdx - 1 }, ext: { width: 100, height: 75 } });
            needsTallRow = true;
          } catch (_e) { /* keep text only */ }
        }
        if (needsTallRow) row.height = 58;
        if (lateInReason)  row.getCell(11).fill  = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
        if (lateOutReason) row.getCell(12).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFEDD5' } };
        if (i % 2 === 0)   row.fill            = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
        if (missingPunch) {
          for (let c = 1; c <= 20; c += 1) {
            const cell = row.getCell(c);
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFECACA' } };
            cell.font = { ...(cell.font || {}), bold: true, color: { argb: 'FF991B1B' } };
          }
        }
      }

      const totalScheduledExcel = Object.values(scheduledHoursByRole).reduce((s, h) => s + h, 0);
      const breakH = Math.floor(totalBreakMin / 60);
      const breakM = totalBreakMin % 60;
      worksheet.addRow([]);
      const totalRow = worksheet.addRow([
        'TOTAL', '',
        totalScheduledExcel > 0 ? `${totalScheduledExcel.toFixed(2)} hrs scheduled` : '',
        '', '', totalGross.toFixed(2), `${totalBreakMin} min`, totalHours.toFixed(2),
        '', '', '', '', '', '', '', '', '', '',
        '', totalPay > 0 ? `£${totalPay.toFixed(2)}` : '',
      ]);
      totalRow.font = { bold: true };
      totalRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFACC15' } };
      // Clear summary block (per break-deduction spec)
      const summaryRows = [
        ['TOTAL SHIFTS WORKED', String(shiftsWorkedCount)],
        ['TOTAL GROSS HOURS', totalGross.toFixed(2)],
        ['TOTAL BREAK TIME', `${breakH} hours ${breakM} minutes`],
        ['TOTAL NET WORKED HOURS', totalHours.toFixed(2)],
      ];
      summaryRows.forEach(([label, value]) => {
        const r = worksheet.addRow([label, value]);
        r.getCell(1).font = { bold: true };
        r.getCell(2).font = { bold: true };
        r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
        r.getCell(2).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
      });
      const breakNote = worksheet.addRow(['Breaks are deducted automatically: <4h = 0 min · 4–6h = 20 min · >6–8h = 30 min · >8h = 60 min. * = Primary Admin override.']);
      breakNote.font = { italic: true, size: 9, color: { argb: 'FF6B7280' } };
      const verifyRow = worksheet.addRow([
        `AUTO-VERIFICATION: ${verifyOk + verifyDiff} rows checked against recorded punches · ${verifyOk} verified · ${verifyDiff} flagged`,
      ]);
      verifyRow.font = { bold: true, italic: true, color: { argb: verifyDiff > 0 ? 'FF991B1B' : 'FF16A34A' } };

      const empNameUpper = (selectedEmployee.name || '').toUpperCase();
      const breakdown = Object.entries(hoursByRole).sort((a, b) => b[1] - a[1]);
      if (breakdown.length > 0) {
        worksheet.addRow([]);
        const headerByRole = worksheet.addRow(['HOURS BY ROLE (WORKED)', empNameUpper, '', '', '', '', '', '', '', '', '', '', '']);
        headerByRole.font = { bold: true, size: 12 };
        headerByRole.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE5E7EB' } };
        breakdown.forEach(([role, hrs]) => {
          const r = worksheet.addRow([role, '', '', '', '', '', '', hrs.toFixed(2), '', '', '', '', '']);
          r.getCell(1).font = { bold: true };
          r.getCell(8).font = { bold: true };
        });
      }

      const schedBreakdown = Object.entries(scheduledHoursByRole).sort((a, b) => b[1] - a[1]);
      if (schedBreakdown.length > 0) {
        worksheet.addRow([]);
        const headerSched = worksheet.addRow(['HOURS BY ROLE (SCHEDULED)', empNameUpper, '', '', '', '', '', '', '', '', '', '', '']);
        headerSched.font = { bold: true, size: 12 };
        headerSched.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDBEAFE' } };
        let totalSched = 0;
        schedBreakdown.forEach(([role, hrs]) => {
          totalSched += hrs;
          const r = worksheet.addRow([role, '', '', '', '', hrs.toFixed(2), '', '', '', '', '', '', '']);
          r.getCell(1).font = { bold: true };
          r.getCell(6).font = { bold: true };
        });
        const totalSchedRow = worksheet.addRow(['TOTAL SCHEDULED', '', '', '', '', totalSched.toFixed(2), '', '', '', '', '', '', '']);
        totalSchedRow.font = { bold: true };
        totalSchedRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBFDBFE' } };
      }

      // Data quality warning — days that carried scheduled_start OR _end but
      // couldn't be parsed into HH:MM. These are the reason "20 × 12 ≠ 240"
      // reports usually happen and are almost always fixable by editing the
      // shift's times in the Shift Calendar.
      if (skippedScheduleRows.length > 0) {
        worksheet.addRow([]);
        const warnHeader = worksheet.addRow([
          'DATA WARNING — Days skipped from scheduled total',
          '', '', '', '', '', '', '', '', '', '', '', ''
        ]);
        warnHeader.font = { bold: true, size: 12, color: { argb: 'FF991B1B' } };
        warnHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
        skippedScheduleRows.forEach((sk) => {
          const r = worksheet.addRow([
            sk.date || '',
            'unparseable times',
            `start="${sk.raw_start}"`,
            `end="${sk.raw_end}"`,
            '', '', '', '', '', '', '', '', '',
          ]);
          r.getCell(1).font = { italic: true, color: { argb: 'FF991B1B' } };
        });
        const explanationRow = worksheet.addRow([
          `${skippedScheduleRows.length} day(s) missing valid times — fix in Shift Calendar to update this total.`,
          '', '', '', '', '', '', '', '', '', '', '', ''
        ]);
        explanationRow.font = { italic: true, size: 10, color: { argb: 'FF991B1B' } };
      }

      // Worked-hours warnings: rows dropped from the worked total because the
      // clock in/out timestamps are unusable. Common causes: stale clock-in
      // never closed (>48h span), clock-out timestamp before clock-in
      // (corrupt), or timestamp field completely unparseable.
      if (skippedWorkedRows.length > 0) {
        worksheet.addRow([]);
        const warnHeader = worksheet.addRow([
          'DATA WARNING — Days skipped from WORKED total',
          '', '', '', '', '', '', '', '', '', '', '', ''
        ]);
        warnHeader.font = { bold: true, size: 12, color: { argb: 'FF991B1B' } };
        warnHeader.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEE2E2' } };
        skippedWorkedRows.forEach((sk) => {
          const r = worksheet.addRow([
            sk.date || '',
            sk.reason || '',
            `in="${sk.raw_in}"`,
            `out="${sk.raw_out}"`,
            '', '', '', '', '', '', '', '', '',
          ]);
          r.getCell(1).font = { italic: true, color: { argb: 'FF991B1B' } };
        });
        const workedExplanation = worksheet.addRow([
          `${skippedWorkedRows.length} day(s) had invalid clock-in/out — review in Clock History to fix.`,
          '', '', '', '', '', '', '', '', '', '', '', ''
        ]);
        workedExplanation.font = { italic: true, size: 10, color: { argb: 'FF991B1B' } };
      }

      worksheet.eachRow((row, rowNumber) => {
        if (rowNumber >= 3) {
          row.eachCell((cell) => {
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFcccccc' } },
              left: { style: 'thin', color: { argb: 'FFcccccc' } },
              bottom: { style: 'thin', color: { argb: 'FFcccccc' } },
              right: { style: 'thin', color: { argb: 'FFcccccc' } },
            };
          });
        }
      });

      const buffer = await workbook.xlsx.writeBuffer();
      saveAs(new Blob([buffer]), `${selectedEmployee.name.replace(/\s+/g, '_')}_${monthLabel}_${selectedYear}.xlsx`);
      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        toast.success('Excel downloaded', {
          description: 'Saved to your Downloads folder — open the download notification or your browser\'s Downloads to view it.',
          duration: 10000,
        });
      } else {
        toast.success('Excel downloaded');
      }
    } catch (error) {
      console.error('Excel Error:', error);
      toast.error('Failed to generate Excel');
    } finally {
      setGeneratingZip(false);
    }
  };

  // ---------- Per-card "Export Payroll" — wraps the existing exporters ----------
  // Loads the employee's history (ref mirror) then exports immediately —
  // no "Open Details first" needed anymore.
  const handleExportForEmployee = async (employeeId, kind = 'excel') => {
    // For PDF: open the tab NOW (synchronously on tap) so mobile browsers don't popup-block it.
    let preWin = null;
    if (kind === 'pdf') {
      preWin = window.open('', '_blank');
      if (preWin) preWin.document.write(REPORT_LOADING_HTML);
    }
    try {
      await loadAndSelect(employeeId);
    } catch (e) {
      if (preWin && !preWin.closed) preWin.close();
      throw e;
    }
    if (kind === 'excel') handleDownloadExcel();
    else handlePrintReport(preWin);
  };

  if (loadingEmployees) {
    return (
      <Layout>
        <div className="p-6 flex items-center justify-center min-h-screen">
          <div className="animate-pulse text-muted-foreground">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-3 sm:p-6 space-y-5 min-h-screen">
        {/* Header */}
        <div className="border-b border-border pb-4 sm:pb-6">
          <h1 className="text-xl sm:text-2xl font-barlow tracking-widest text-foreground flex items-center gap-3">
            <Clock className="w-5 h-5 sm:w-6 sm:h-6 text-primary" strokeWidth={1.5} />
            {category === 'retail' ? 'RETAIL CLOCK HISTORY' : category === 'event' ? 'EVENT CLOCK HISTORY' : combined ? 'PAYROLL CLOCK IN HISTORY' : 'CLOCK HISTORY'}
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground mt-1">
            {category
              ? 'Per-site scheduled vs worked hours for invoicing, plus the staff roster for this category.'
              : combined
              ? 'All staff in one place — retail and event work combined. One Export Payroll file per employee covers every location worked that month.'
              : 'Payroll-focused view by month. Mirrors Shift Calendar — every employee with shifts or clock activity appears here.'}
          </p>
        </div>

        {/* Month selector + search */}
        <Card className="bg-card border-border rounded-sm" data-testid="month-selector-card">
          <CardContent className="p-3 sm:p-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="col-span-2 sm:col-span-1 space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <CalendarRange className="w-3.5 h-3.5" /> Month
                </Label>
                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger className="bg-background border-border rounded-sm" data-testid="select-month">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {MONTHS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Year</Label>
                <Select value={selectedYear} onValueChange={setSelectedYear}>
                  <SelectTrigger className="bg-background border-border rounded-sm" data-testid="select-year">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    {yearOptions.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2 sm:col-span-2 space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Search className="w-3.5 h-3.5" /> Employee search
                </Label>
                <Input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="One name, or several separated by commas…"
                  className="bg-background border-border rounded-sm"
                  data-testid="employee-search-input"
                />
              </div>
              <div className="col-span-2 sm:col-span-2 space-y-1">
                <Label className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" /> Specific day (optional)
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="date"
                    value={dayDate}
                    onChange={(e) => setDayDate(e.target.value)}
                    className="bg-background border-border rounded-sm"
                    data-testid="day-search-input"
                  />
                  {dayDate && (
                    <Button variant="outline" size="sm" onClick={() => setDayDate('')} data-testid="day-search-clear" className="rounded-sm h-10">
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Filter chips */}
            <div className="flex flex-wrap gap-2 pt-1" data-testid="filters-chip-row">
              {[
                { id: 'all',        label: 'All staff' },
                { id: 'manual',     label: 'Manual only' },
                { id: 'missingGps', label: 'Missing GPS' },
                { id: 'autoOut',    label: 'Auto clock-outs' },
                { id: 'edited',     label: 'Edited records' },
              ].map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setFilter(f.id)}
                  data-testid={`filter-${f.id}`}
                  className={cn(
                    'px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border whitespace-nowrap transition-colors',
                    filter === f.id
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'bg-card text-muted-foreground border-border hover:bg-accent hover:text-foreground'
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Site totals — Retail / Event category pages only */}
        {category && (
          <div className="space-y-2">
            <p className="text-xs uppercase tracking-widest text-muted-foreground font-barlow">
              Sites — scheduled vs worked hours
            </p>
            {siteTotalsLoading && !siteTotals ? (
              <Card className="bg-card border-border rounded-sm">
                <CardContent className="p-6 text-center text-muted-foreground animate-pulse text-sm">Loading site totals…</CardContent>
              </Card>
            ) : (
              <ClockSiteTotals
                data={siteTotals}
                month={`${selectedYear}-${selectedMonth}`}
                category={category}
                onOpenEmployee={openEmployeeFromSite}
                onChanged={fetchSiteTotals}
                readOnly={payrollMode}
                linkBase={payrollMode ? '/employee/pay/clock-history' : '/admin/clock-history'}
              />
            )}
          </div>
        )}

        {/* Day search results — punches for the chosen date, narrowed by the
            employee search box (comma-separated names = group search). */}
        {dayDate && (
          <Card className="bg-card border-primary/30 rounded-sm" data-testid="day-results-card">
            <CardContent className="p-4 space-y-3">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-barlow tracking-wider uppercase text-primary">
                  Day search — {formatUK(`${dayDate}T12:00:00`, 'dd MMM yyyy')}
                </p>
                <span className="text-xs text-muted-foreground">
                  {dayLoading ? 'Loading…' : `${dayResults.length} employee(s)`}
                </span>
              </div>
              {dayLoading ? (
                <p className="text-sm text-muted-foreground animate-pulse py-2">Loading punches…</p>
              ) : dayResults.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2" data-testid="day-results-empty">
                  No clock activity on this day{search.trim() ? ' for the searched name(s)' : ''}.
                </p>
              ) : (
                <div className="space-y-2">
                  {dayResults.map((row) => (
                    <div key={row.id} className="border border-border rounded-sm p-3 bg-muted/20" data-testid={`day-row-${row.id}`}>
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-medium text-foreground flex items-center gap-2">
                          {row.name}
                          <DayPdfButton employeeId={row.id} employeeName={row.name} date={dayDate} />
                        </p>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 rounded-sm text-[10px] uppercase tracking-wider"
                          onClick={() => handleToggleDetails(row.id)}
                          data-testid={`day-row-view-${row.id}`}
                        >
                          View full month
                        </Button>
                      </div>
                      <div className="mt-2 space-y-1">
                        {row.pairs.map((p, i) => {
                          const ev = p.in || p.out;
                          const manual = (p.in && (p.in.allocated_by || p.in.manual_clock_in || p.in.admin_override)) || (p.out && p.out.admin_override);
                          const noGps = (p.in && p.in.latitude == null) || (p.out && p.out.latitude == null);
                          return (
                            <div key={ev?.id || i} className="flex flex-wrap items-center gap-2 text-sm font-mono">
                              <span className="text-green-400">{p.in ? formatUK(p.in.timestamp, 'HH:mm') : '—'}</span>
                              <span className="text-muted-foreground">→</span>
                              <span className="text-red-400">{p.out ? formatUK(p.out.timestamp, 'HH:mm') : '—'}</span>
                              {p.in && p.out && (
                                <span className="text-xs text-muted-foreground">
                                  ({((new Date(p.out.timestamp) - new Date(p.in.timestamp)) / 3600000).toFixed(1)}h)
                                </span>
                              )}
                              {manual && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-400 border border-amber-500/30 font-sans">Manual</span>}
                              {noGps && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-400 border border-red-500/30 font-sans">No GPS</span>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Period summary — shows the *full* count of staff with any shift
            activity in the selected month (clock-ins, scheduled shifts, or
            claimed open shifts — i.e. the same population as the Shift
            Calendar for that month), so admin always has the headline number
            visible even if a search/filter is active. */}
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-testid="period-summary">
          <UsersIcon className="w-3.5 h-3.5" />
          <span>
            <strong className="text-primary">{workedThisMonth.length}</strong>
            {' '}employee(s) with shift activity in{' '}
            <strong className="text-foreground">{monthLabel} {selectedYear}</strong>
            {search.trim() && (
              <span className="ml-2 opacity-80">
                · showing <strong className="text-foreground">{visibleRoster.length}</strong> match{visibleRoster.length === 1 ? '' : 'es'} for &ldquo;{search.trim()}&rdquo;
              </span>
            )}
          </span>
        </div>

        {/* Roster
            Strategy: as soon as we have employees for the selected month
            we render them — even if a re-fetch is in flight in the
            background. This eliminates a stale-loading-flag race where the
            cards would never appear because `activeLoading` got stuck on
            `true` after a rapid month change. A small refresh chip surfaces
            the loading state without hiding the data. */}
        {activeLoading && workedThisMonth.length === 0 ? (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-8 text-center text-muted-foreground animate-pulse">Loading payroll data…</CardContent>
          </Card>
        ) : visibleRoster.length === 0 ? (
          <Card className="bg-card border-border rounded-sm">
            <CardContent className="p-8 text-center text-muted-foreground">
              <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">
                {search.trim()
                  ? `No employees match "${search.trim()}" in ${monthLabel} ${selectedYear}.`
                  : workedThisMonth.length === 0
                    ? `No staff worked in ${monthLabel} ${selectedYear}.`
                    : `No employees match the current filter for ${monthLabel} ${selectedYear}. Switch to "All staff" to see everyone who worked.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3" data-testid="employee-cards">
            {activeLoading && (
              <p className="text-[11px] text-muted-foreground italic animate-pulse">Refreshing…</p>
            )}
            {visibleRoster.map((emp) => (
              <div key={emp.id} id={`emp-card-${emp.id}`}>
                <EmployeePayrollCard
                  emp={emp}
                  expanded={expandedId === emp.id}
                  stats={statsFor(emp.id)}
                  loading={loadingViewId === emp.id}
                  onToggle={() => handleToggleDetails(emp.id)}
                  onExportExcel={() => handleExportForEmployee(emp.id, 'excel')}
                  onExportPdf={() => handleExportForEmployee(emp.id, 'pdf')}
                  generating={generatingZip}
                  history={historyCache[`${emp.id}|${selectedYear}-${selectedMonth}`]}
                  filterFn={FILTERS[filter]}
                  onEditEvent={payrollMode ? null : handleEditClick}
                  onBreakOverride={!payrollMode && isPrimaryAdmin ? openBreakDialog : null}
                  onWorkHours={payrollMode ? () => setWorkHoursFor(emp) : null}
                  onStaffInfo={payrollMode ? () => setStaffInfoFor(emp) : null}
                  onDeleteEvent={payrollMode ? null : handleDeleteClick}
                  readOnly={payrollMode}
                />
              </div>
            ))}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={!!editingEvent} onOpenChange={(open) => !open && handleCancelEdit()}>
          <DialogContent className="bg-card border-border rounded-sm">
            <DialogHeader><DialogTitle className="text-primary">Edit Time</DialogTitle></DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input type="date" value={editDate} onChange={(e) => setEditDate(e.target.value)} className="bg-background border-border" />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input type="time" value={editTime} onChange={(e) => setEditTime(e.target.value)} className="bg-background border-border" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleCancelEdit} className="border-border">
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
              <Button onClick={handleSaveEdit} disabled={saving} className="bg-primary text-primary-foreground">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Break override dialog (Primary Admin only) */}
        <Dialog open={!!breakDialog} onOpenChange={(o) => { if (!o) setBreakDialog(null); }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md" data-testid="break-override-dialog">
            <DialogHeader>
              <DialogTitle className="text-primary font-barlow tracking-wider">OVERRIDE BREAK</DialogTitle>
            </DialogHeader>
            {breakDialog && (
              <div className="space-y-3">
                <div className="text-xs text-muted-foreground space-y-0.5 bg-muted/30 border border-border/60 rounded-sm p-2">
                  <p>Date: <span className="text-foreground font-mono">{breakDialog.entry.date}</span></p>
                  <p>Gross hours: <span className="text-foreground font-mono">{(breakDialog.entry.gross_hours ?? 0).toFixed(2)}h</span></p>
                  <p>Automatic break: <span className="text-foreground font-mono">{breakDialog.entry.auto_break_minutes ?? 0} min</span></p>
                  <p>Current break: <span className="text-foreground font-mono">{breakDialog.entry.break_minutes ?? 0} min{breakDialog.entry.break_overridden ? ' (override)' : ' (automatic)'}</span></p>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">New break duration</Label>
                  <div className="grid grid-cols-6 gap-1.5 mt-1">
                    {['0', '20', '30', '45', '60', 'custom'].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setBreakMinutesSel(v)}
                        className={cn(
                          'px-1 py-1.5 text-xs rounded-sm border font-mono',
                          breakMinutesSel === v
                            ? 'border-primary bg-primary/15 text-primary font-semibold'
                            : 'border-border text-muted-foreground hover:border-primary/50'
                        )}
                        data-testid={`break-opt-${v}`}
                      >
                        {v === 'custom' ? 'Other' : `${v}m`}
                      </button>
                    ))}
                  </div>
                  {breakMinutesSel === 'custom' && (
                    <Input
                      type="number" min="0" max="480" value={breakCustom}
                      onChange={(e) => setBreakCustom(e.target.value)}
                      placeholder="Minutes (0–480)"
                      className="mt-2 bg-background border-border rounded-sm"
                      data-testid="break-custom-input"
                    />
                  )}
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">Reason (mandatory)</Label>
                  <Input
                    value={breakReason}
                    onChange={(e) => setBreakReason(e.target.value)}
                    placeholder="e.g. Paid break authorised for this event"
                    className="mt-1 bg-background border-border rounded-sm"
                    data-testid="break-reason-input"
                  />
                </div>
                <div className="flex gap-2 pt-1">
                  <Button
                    onClick={handleSaveBreak} disabled={breakSaving}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm"
                    data-testid="save-break-override-btn"
                  >
                    {breakSaving ? 'Saving…' : 'Save Override'}
                  </Button>
                  {breakDialog.entry.break_overridden && (
                    <Button
                      variant="outline" onClick={handleResetBreak} disabled={breakSaving}
                      className="border-border text-foreground hover:bg-accent rounded-sm"
                      data-testid="reset-break-override-btn"
                    >
                      Restore Automatic
                    </Button>
                  )}
                </div>
                <p className="text-[10px] text-muted-foreground italic">
                  The override is audited (who, when, reason) and flows automatically into Clock History, Excel, Payroll, Holiday Entitlement and Work Hours History.
                </p>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Staff Details dialog (Payroll department — read-only) */}
        <Dialog open={!!staffInfoFor} onOpenChange={(o) => { if (!o) setStaffInfoFor(null); }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md max-h-[85vh] overflow-y-auto" data-testid="staff-info-dialog">
            <DialogHeader>
              <DialogTitle className="text-primary font-barlow tracking-wider">
                STAFF DETAILS — {staffInfoFor?.name}
              </DialogTitle>
            </DialogHeader>
            {staffInfoFor && <StaffPayrollInfo employeeId={staffInfoFor.id} />}
          </DialogContent>
        </Dialog>

        {/* Work Hours History dialog (Payroll department) */}
        <Dialog open={!!workHoursFor} onOpenChange={(o) => { if (!o) setWorkHoursFor(null); }}>
          <DialogContent className="bg-card border-border rounded-sm max-w-md max-h-[85vh] overflow-y-auto" data-testid="work-hours-dialog">
            <DialogHeader>
              <DialogTitle className="text-primary font-barlow tracking-wider">
                WORK HOURS HISTORY — {workHoursFor?.name}
              </DialogTitle>
            </DialogHeader>
            {workHoursFor && <WorkHoursHistory employeeId={workHoursFor.id} />}
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Event</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this event? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-border">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirmDelete} disabled={deleting} className="bg-destructive">
                {deleting ? 'Deleting...' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}

// =========================================================================
// Per-employee card
// =========================================================================
function EmployeePayrollCard({
  emp, expanded, stats, loading, onToggle, onExportExcel, onExportPdf,
  generating, history, filterFn, onEditEvent, onDeleteEvent, readOnly = false, onWorkHours = null,
  onBreakOverride = null, onStaffInfo = null,
}) {
  // Stats from the active-employees endpoint are always available; the
  // richer per-flag stats come from the loaded history (once expanded).
  const baselineHours = emp.worked_hours || 0;
  const baselineDays = emp.days_worked || 0;
  const displayHours = stats ? stats.totalHours.toFixed(2) : baselineHours.toFixed(2);
  const displayShifts = stats ? stats.shifts : baselineDays;

  // A "scheduled-but-not-clocked-in" employee has shifts in the month but
  // zero clock activity. We surface this clearly so admin can spot missing
  // payroll inputs from the Shift Calendar at a glance.
  const noClock = !emp.has_clock && (baselineHours === 0) && (baselineDays === 0);

  return (
    <Card
      className={cn(
        'bg-card rounded-sm',
        noClock ? 'border-amber-500/50' : 'border-border'
      )}
      data-testid={`payroll-card-${emp.id}`}
    >
      <CardContent className="p-3 sm:p-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className={cn(
            'w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0',
            noClock ? 'bg-amber-500/10 text-amber-500' : 'bg-primary/10 text-primary'
          )}>
            <User className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            {readOnly ? (
              <p className="text-sm sm:text-base font-medium text-foreground truncate" data-testid={`payroll-name-link-${emp.id}`}>
                {emp.name}
              </p>
            ) : (
              <Link
                to={`/admin/employees/${emp.id}?tab=schedule`}
                className="text-sm sm:text-base font-medium text-foreground truncate block hover:text-primary hover:underline underline-offset-2"
                title="Open employee profile — Schedule"
                onClick={(e) => e.stopPropagation()}
                data-testid={`payroll-name-link-${emp.id}`}
              >
                {emp.name}
              </Link>
            )}
            {!readOnly && <ComplianceIndicator userId={emp.id} />}
            <p className="text-[11px] text-muted-foreground truncate">{emp.email}</p>
            {noClock && (
              <Badge className="mt-1 text-[9px] uppercase tracking-wider bg-amber-500/15 text-amber-500 border-amber-500/40">
                <AlertTriangle className="w-2.5 h-2.5 mr-1" /> Scheduled · no clock-in yet
              </Badge>
            )}
          </div>
          <div className="text-right flex-shrink-0">
            <p className={cn(
              'font-mono text-lg sm:text-xl leading-none',
              noClock ? 'text-muted-foreground' : 'text-primary'
            )}>
              {displayHours}h
            </p>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mt-1">Total worked (net)</p>
            {typeof emp.gross_hours === 'number' && emp.gross_hours > 0 && (
              <p className="text-[10px] text-muted-foreground mt-0.5 font-mono" data-testid={`gross-break-${emp.id}`}>
                Gross {emp.gross_hours.toFixed(2)}h · Break {emp.break_minutes || 0}m
              </p>
            )}
            {emp.scheduled_hours > 0 && (
              <p className="text-[10px] text-muted-foreground mt-0.5">
                of {emp.scheduled_hours.toFixed(2)}h scheduled
              </p>
            )}
          </div>
        </div>

        {/* Stat tiles */}
        <div className="mt-3 grid grid-cols-3 md:grid-cols-5 gap-2" data-testid={`stat-tiles-${emp.id}`}>
          <StatTile label="Shifts"      value={displayShifts}              />
          <StatTile label="Manual"      value={stats?.manual ?? '—'}      tone={stats && stats.manual > 0 ? 'amber' : 'muted'} />
          <StatTile label="Auto out"    value={stats?.autoOuts ?? '—'}    tone={stats && stats.autoOuts > 0 ? 'red' : 'muted'} />
          <StatTile label="No GPS"      value={stats?.missingGps ?? '—'}  tone={stats && stats.missingGps > 0 ? 'amber' : 'muted'} />
          <StatTile label="Edited"      value={stats?.edited ?? '—'}      tone={stats && stats.edited > 0 ? 'blue' : 'muted'} />
        </div>

        {/* Actions */}
        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            variant="outline" size="sm"
            onClick={onToggle}
            disabled={loading}
            className="border-primary text-primary hover:bg-primary/10 rounded-sm flex-1 sm:flex-none"
            data-testid={`view-details-${emp.id}`}
          >
            {expanded ? <ChevronUp className="w-4 h-4 mr-1" /> : <ChevronDown className="w-4 h-4 mr-1" />}
            {loading ? 'Loading…' : (expanded ? 'Hide Details' : 'View Details')}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={onExportExcel}
            disabled={generating}
            className="border-green-600 text-green-500 hover:bg-green-900/20 rounded-sm flex-1 sm:flex-none"
            data-testid={`export-payroll-${emp.id}`}
          >
            <FileArchive className="w-4 h-4 mr-1" />
            {generating ? 'Generating…' : 'Export Payroll'}
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={onExportPdf}
            className="border-border text-foreground hover:bg-accent rounded-sm flex-1 sm:flex-none"
            data-testid={`print-pdf-${emp.id}`}
          >
            <Printer className="w-4 h-4 mr-1" /> Print PDF
          </Button>
          {onWorkHours && (
            <Button
              variant="outline" size="sm"
              onClick={onWorkHours}
              className="border-blue-600 text-blue-400 hover:bg-blue-900/20 rounded-sm flex-1 sm:flex-none"
              data-testid={`work-hours-${emp.id}`}
            >
              <Clock className="w-4 h-4 mr-1" /> Work Hours History
            </Button>
          )}
          {onStaffInfo && (
            <Button
              variant="outline" size="sm"
              onClick={onStaffInfo}
              className="border-purple-600 text-purple-400 hover:bg-purple-900/20 rounded-sm flex-1 sm:flex-none"
              data-testid={`staff-info-${emp.id}`}
            >
              <User className="w-4 h-4 mr-1" /> Staff Details
            </Button>
          )}
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="mt-4 border-t border-border pt-4 space-y-2" data-testid={`details-${emp.id}`}>
            {!history ? (
              <p className="text-sm text-muted-foreground italic">Loading history…</p>
            ) : (
              (() => {
                const visible = (history || []).filter(filterFn);
                if (visible.length === 0) {
                  return <p className="text-sm text-muted-foreground italic">No matching records under the current filter.</p>;
                }
                return visible.map((entry, idx) => (
                  <ClockEntryRow
                    key={`${emp.id}-${idx}`}
                    entry={entry}
                    employeeId={emp.id}
                    onEdit={onEditEvent}
                    onDelete={onDeleteEvent}
                    onBreakOverride={onBreakOverride}
                  />
                ));
              })()
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StatTile({ label, value, tone = 'muted' }) {
  const tones = {
    muted: 'bg-muted/40 text-foreground',
    amber: 'bg-amber-500/15 text-amber-500',
    red:   'bg-red-500/15 text-red-500',
    blue:  'bg-blue-500/15 text-blue-500',
  };
  return (
    <div className={cn('px-2 py-1.5 rounded-sm border border-border/60', tones[tone])}>
      <p className="font-mono text-sm font-semibold leading-tight">{value}</p>
      <p className="text-[9px] uppercase tracking-wider opacity-80">{label}</p>
    </div>
  );
}

// =========================================================================
// Clock entry row (inside a card's expanded details)
// =========================================================================
function ClockEntryRow({ entry, employeeId, onEdit, onDelete, onBreakOverride }) {
  const [photoView, setPhotoView] = useState({ open: false, src: null, label: '' });
  const hasBreakInfo = typeof entry.net_hours === 'number';
  let hoursWorked = '--';
  if (!hasBreakInfo && entry.clock_in && entry.clock_out) {
    const diff = (new Date(entry.clock_out.timestamp) - new Date(entry.clock_in.timestamp)) / 3600000;
    hoursWorked = diff.toFixed(2) + ' hrs';
  }
  const openPhoto = async (eventId, label) => {
    try {
      const r = await axios.get(`${API}/admin/clock/photo/${eventId}`);
      if (r.data?.photo_url && r.data.photo_url !== 'has_photo') {
        setPhotoView({ open: true, src: r.data.photo_url, label });
      } else {
        toast.error('No photo available');
      }
    } catch { toast.error('Failed to load photo'); }
  };

  return (
    <div className="bg-background border border-border rounded-sm p-3" data-testid={`entry-${entry.date}-${employeeId}`}>
      {/* Top line: date, location, role */}
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <p className="text-sm font-medium text-foreground">
          {format(parseISO(entry.date), 'EEE, dd MMM yyyy')}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {entry.site_name && (
            <Badge variant="outline" className="text-[10px] flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {entry.site_name}
            </Badge>
          )}
          {(entry.clock_in?.shift_role || entry.shift_role) && (
            <Badge className="text-[10px] bg-blue-500/10 text-blue-400 border border-blue-500/40">
              {entry.clock_in?.shift_role || entry.shift_role}
            </Badge>
          )}
        </div>
      </div>

      {/* Scheduled times */}
      {entry.shift_start && entry.shift_end && (
        <p className="text-[11px] text-muted-foreground mb-2">
          Scheduled: <span className="font-mono">{entry.shift_start} – {entry.shift_end}</span>
        </p>
      )}

      {/* Clock In / Out row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
        <ClockHalf
          icon={ArrowUpRight} tone="green" label="In"
          event={entry.clock_in} employeeId={employeeId}
          openPhoto={openPhoto} onEdit={onEdit} onDelete={onDelete}
        />
        <ClockHalf
          icon={ArrowDownRight} tone="red" label="Out"
          event={entry.clock_out} employeeId={employeeId}
          openPhoto={openPhoto} onEdit={onEdit} onDelete={onDelete}
        />
        {hasBreakInfo ? (
          <div className="font-mono text-xs space-y-0.5" data-testid={`hours-breakdown-${entry.date}-${employeeId}`}>
            <p className="text-muted-foreground">Gross: <span className="text-foreground font-semibold">{entry.gross_hours.toFixed(2)}h</span></p>
            <p className="text-muted-foreground flex items-center gap-1 flex-wrap">
              Break: <span className={entry.break_overridden ? 'text-blue-400 font-semibold' : 'text-foreground font-semibold'}>
                {entry.break_minutes} min{entry.break_overridden ? ' (override)' : ''}
              </span>
              {onBreakOverride && (
                <button
                  type="button"
                  onClick={() => onBreakOverride(entry, employeeId)}
                  className="text-[10px] uppercase tracking-wider text-primary underline underline-offset-2 hover:text-yellow-300 ml-1"
                  data-testid={`override-break-${entry.date}-${employeeId}`}
                >
                  Edit
                </button>
              )}
            </p>
            <p className="text-primary font-semibold text-sm flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" /> Net: {entry.net_hours.toFixed(2)}h
            </p>
          </div>
        ) : (
          <div className="flex items-center gap-2 text-primary font-medium font-mono">
            <Clock className="w-3.5 h-3.5" /> {hoursWorked}
          </div>
        )}
      </div>

      {hasBreakInfo && entry.break_override && (
        <div className="mt-2 flex items-start gap-2 text-xs bg-blue-500/10 border-l-2 border-blue-500/60 px-2 py-1.5 rounded-sm">
          <span className="font-semibold text-blue-400 shrink-0">Break override:</span>
          <span className="text-foreground/90">
            {entry.break_override.reason} — {entry.break_override.admin_name}
          </span>
        </div>
      )}

      {/* Flags */}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(entry.clock_in?.manual_clock_in || entry.clock_in?.allocated_by) && (
          <Badge className="text-[10px] uppercase tracking-wider bg-amber-500/15 text-amber-500 border-amber-500/40">
            <AlertTriangle className="w-2.5 h-2.5 mr-1" /> Manual
          </Badge>
        )}
        {entry.clock_out?.auto_clock_out && (
          <Badge className="text-[10px] uppercase tracking-wider bg-red-500/15 text-red-500 border-red-500/40">
            <AlertTriangle className="w-2.5 h-2.5 mr-1" /> Auto Out
          </Badge>
        )}
        {entry.clock_out?.forgot_clock_out && (
          <Badge className="text-[10px] uppercase tracking-wider bg-yellow-500/15 text-yellow-500 border-yellow-500/40">
            <AlertTriangle className="w-2.5 h-2.5 mr-1" /> Forgot Out
          </Badge>
        )}
        {(entry.clock_in?.edited_by_admin || entry.clock_out?.edited_by_admin) && (
          <Badge className="text-[10px] uppercase tracking-wider bg-blue-500/15 text-blue-500 border-blue-500/40">
            Edited
          </Badge>
        )}
      </div>

      {/* Late reasons */}
      {(entry.clock_in?.late_reason || entry.clock_out?.late_reason) && (
        <div className="mt-2 space-y-1.5 border-t border-border pt-2">
          {entry.clock_in?.late_reason && (
            <div className="flex items-start gap-2 text-xs bg-amber-500/10 border-l-2 border-amber-500/60 px-2 py-1.5 rounded-sm">
              <span className="font-semibold text-amber-400 shrink-0">Late In:</span>
              <span className="text-foreground/90">{entry.clock_in.late_reason}</span>
            </div>
          )}
          {entry.clock_out?.late_reason && (
            <div className="flex items-start gap-2 text-xs bg-orange-500/10 border-l-2 border-orange-500/60 px-2 py-1.5 rounded-sm">
              <span className="font-semibold text-orange-400 shrink-0">Late Out:</span>
              <span className="text-foreground/90">{entry.clock_out.late_reason}</span>
            </div>
          )}
        </div>
      )}

      {/* Notes */}
      {entry.clock_in?.notes && (
        <p className="mt-2 text-[11px] text-muted-foreground italic">{entry.clock_in.notes}</p>
      )}

      {/* Photo viewer */}
      {photoView.open && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPhotoView({ open: false, src: null, label: '' })}>
          <div className="max-w-2xl max-h-[80vh] relative">
            <img src={photoView.src} alt={photoView.label} className="max-w-full max-h-[80vh] object-contain rounded-sm" />
            <Button variant="destructive" size="icon" className="absolute top-2 right-2" onClick={() => setPhotoView({ open: false, src: null, label: '' })}>
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ClockHalf({ icon: Icon, tone, label, event, employeeId, openPhoto, onEdit, onDelete }) {
  const colorClass = tone === 'green' ? 'text-green-400' : 'text-red-400';
  const time = event ? formatUK(event.timestamp) : '--:--';
  const hasPin = event && typeof event.latitude === 'number' && typeof event.longitude === 'number';
  const hasPhoto = event && event.photo_url === 'has_photo';
  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      <Icon className={cn('w-3.5 h-3.5', colorClass)} />
      <span className={cn('text-[10px] uppercase tracking-wider', colorClass)}>{label}</span>
      <span className="text-sm font-mono text-foreground">{time}</span>
      {event?.actual_timestamp && (
        <span
          className="text-[10px] text-muted-foreground font-mono"
          title="Employee clocked out after shift end — time capped at scheduled end"
          data-testid={`actual-time-${event.id}`}
        >
          (real {formatUK(event.actual_timestamp)})
        </span>
      )}
      {event && (
        <>
          {hasPin ? (
            <a
              href={`https://www.google.com/maps?q=${event.latitude},${event.longitude}`}
              target="_blank" rel="noopener noreferrer"
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-primary/10 text-primary border border-primary/30 rounded-sm hover:bg-primary/20 flex items-center gap-1"
              data-testid={`map-link-${event.id}`}
              title={`${event.latitude.toFixed(5)}, ${event.longitude.toFixed(5)}`}
            >
              <MapPin className="w-2.5 h-2.5" /> Map
            </a>
          ) : (
            <span
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 bg-muted/30 text-muted-foreground border border-border rounded-sm"
              data-testid={`map-missing-${event.id}`}
            >
              No GPS
            </span>
          )}
          {hasPhoto && (
            <Button
              variant="ghost" size="icon"
              onClick={() => openPhoto(event.id, `${label} Photo`)}
              className="h-6 w-6 p-0 hover:bg-blue-500/10"
              data-testid={`photo-btn-${event.id}`}
              title="View photo"
            >
              <Camera className="w-3 h-3 text-blue-400" />
            </Button>
          )}
          {onEdit && (
            <Button
              variant="ghost" size="icon"
              onClick={() => onEdit({ ...event, employee_id: employeeId })}
              className="h-6 w-6 p-0"
              data-testid={`edit-event-${event.id}`}
              title={`Edit ${label}`}
            >
              <Edit2 className="w-3 h-3" />
            </Button>
          )}
          {onDelete && (
            <Button
              variant="ghost" size="icon"
              onClick={() => onDelete({ ...event, employee_id: employeeId })}
              className="h-6 w-6 p-0 text-red-400"
              data-testid={`delete-event-${event.id}`}
              title={`Delete ${label}`}
            >
              <Trash2 className="w-3 h-3" />
            </Button>
          )}
        </>
      )}
    </div>
  );
}
